import { useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { Button, Form, Icon, Label, Modal, Segment } from "semantic-ui-react";

import type { OpenAPI } from "simplydo/interfaces";
import { CheckboxHeader } from "simplydo/react/components/CheckboxHeader";
import { RadioCard } from "simplydo/react/components/RadioCard";
import { RichText, RichTextViewer } from "simplydo/react/richtext";

import api from "api";
import util from "utils/utils";

import ConfigurableTable from "components/lib/ConfigurableTable";
import { BasicAccordion } from "components/lib/UI";

type SendAssessmentFeedbackModalProps = {
  idea: OpenAPI.Assign<
    OpenAPI.Schemas["Idea"],
    OpenAPI.Schemas["AssignIdeaAssessmentDetails"]
  >;
  challenge: OpenAPI.Assign<
    OpenAPI.Schemas["Challenge"],
    OpenAPI.Schemas["AssignChallengeIdeaTemplate"]
  >;
  isOpen: boolean;
  setOpen: (open: boolean) => void;
};

type AssessmentFeedbackTemplateField = {
  id: string;
  type: "assessment" | "assessmentFeedback";
  title?: string;
};

export const SendAssessmentFeedbackModal = ({
  idea,
  challenge,
  isOpen,
  setOpen,
}: SendAssessmentFeedbackModalProps) => {
  const { t } = useTranslation();
  const template = challenge?.ownerIdeaTemplate;
  const assessmentRounds = template?.assessments?.rounds || [];

  const existingRoundIds =
    idea?.assessmentFeedback?.map((f) => f.assessmentRoundId) || [];

  const defaultRoundId =
    assessmentRounds.find((r) => !existingRoundIds.includes(r.id))?.id ||
    assessmentRounds.at(0)?.id ||
    "legacy";

  const [assessmentRoundId, setAssessmentRoundId] = useState(defaultRoundId);
  const [includeOverallScore, setIncludeOverallScore] = useState(true);
  const [fieldSelections, setFieldSelections] = useState<
    Record<string, boolean>
  >({});
  const selectedFieldIds = Object.entries(fieldSelections)
    .filter(([_, value]) => value)
    .map(([key]) => key);

  const [step, setStep] = useState<"configure" | "edit">("configure");
  const [generatedHtml, setGeneratedHtml] = useState("");
  const [feedbackHtml, setFeedbackHtml] = useState("");

  const sentFeedback = idea?.assessmentFeedback?.find(
    (f) => f.assessmentRoundId === assessmentRoundId,
  );

  const sections =
    template?.body?.filter(
      (s) =>
        s.type === "assessment" &&
        (s.assessmentRoundId || "legacy") === assessmentRoundId,
    ) || [];

  const roundFields: Array<{
    sectionName: string;
    field: AssessmentFeedbackTemplateField;
  }> = [];
  for (const section of sections) {
    for (const field of section.fields || []) {
      if (field.type !== "assessment" && field.type !== "assessmentFeedback") {
        continue;
      }
      roundFields.push({
        sectionName: section.name || "Untitled section",
        field: field as AssessmentFeedbackTemplateField,
      });
    }
  }

  const review = () => {
    api.ideas.previewAssessmentFeedback(
      idea._id,
      { assessmentRoundId, includeOverallScore, fieldSelections },
      ({ feedbackHtml }) => {
        const nextHtml = feedbackHtml || "";
        setGeneratedHtml(nextHtml);
        setFeedbackHtml(nextHtml);
        setStep("edit");
      },
      (e) => toast.error(e.message),
    );
  };

  const send = () => {
    api.ideas.sendAssessmentFeedback(
      idea._id,
      { assessmentRoundId, feedbackHtml },
      ({ saved }) => {
        const existing = idea.assessmentFeedback || [];
        const updated = [
          ...existing.filter(
            (e) => e.assessmentRoundId !== saved.assessmentRoundId,
          ),
          saved,
        ];
        util.onUpdateIdea(idea, { assessmentFeedback: updated });
        api.invalidateQueries(api.ideas.get, [idea._id]);
        toast.success("Assessment feedback sent");
        setOpen(false);
        setFeedbackHtml("");
        setGeneratedHtml("");
        setStep("configure");
        setAssessmentRoundId(defaultRoundId);
        setFieldSelections({});
        setIncludeOverallScore(true);
      },
      (e) => toast.error(e.message),
    );
  };

  const roundHasFeedback = (roundId: string) =>
    existingRoundIds.includes(roundId);

  return (
    <>
      <Modal
        open={isOpen}
        size="small"
        mountNode={document.getElementById("semantic-modal-mount-node")}
      >
        <Modal.Header>
          Send {t("generic.idea")} assessment feedback
        </Modal.Header>
        <Modal.Content scrolling>
          {step === "configure" ?
            <>
              <p>
                Use this window to send assessment feedback to the authors of
                this {t("generic.idea")}. Feedback is anonymised and only shown
                to the author of the {t("generic.idea")}.
              </p>

              <h4>Assessment round</h4>
              <RadioCard.Group
                onValueChange={(value) => {
                  setAssessmentRoundId(value);
                  setFieldSelections({});
                  setStep("configure");
                  setGeneratedHtml("");
                  setFeedbackHtml("");
                }}
                defaultValue={defaultRoundId}
                editable
              >
                {assessmentRounds.map((round) => (
                  <RadioCard.Item key={round.id} value={round.id}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexDirection: "column",
                      }}
                    >
                      <span>{round.name}</span>
                      <Label
                        size="tiny"
                        color={roundHasFeedback(round.id) ? "green" : undefined}
                      >
                        <Icon
                          name={
                            roundHasFeedback(round.id) ? "check circle" : (
                              "circle outline"
                            )
                          }
                        />
                        {roundHasFeedback(round.id) ? "Sent" : "Pending"}
                      </Label>
                    </div>
                  </RadioCard.Item>
                ))}
              </RadioCard.Group>

              {sentFeedback ?
                <div style={{ marginTop: 16 }}>
                  <BasicAccordion
                    title="View sent feedback"
                    content={
                      <Segment>
                        <RichTextViewer value={sentFeedback.feedback || ""} />
                      </Segment>
                    }
                  />
                </div>
              : null}

              <CheckboxHeader
                as="h4"
                header="Include overall score"
                description="Include the total weighted score from all assessors in the feedback."
                checked={includeOverallScore}
                onChange={() => setIncludeOverallScore((p) => !p)}
                style={{ marginTop: 24 }}
              />

              <h4 style={{ marginTop: 16 }}>Fields</h4>
              <ConfigurableTable
                tableKey="assessmentFeedbackFields"
                data={roundFields}
                keyExtractor={(item) => item.field.id}
                selectedKeys={selectedFieldIds}
                onSelect={(item) => {
                  if (item === null) {
                    if (selectedFieldIds.length === 0) {
                      setFieldSelections(
                        roundFields.reduce(
                          (acc, item) => {
                            acc[item.field.id] = true;
                            return acc;
                          },
                          {} as Record<string, boolean>,
                        ),
                      );
                    } else {
                      setFieldSelections({});
                    }
                  } else {
                    setFieldSelections({
                      ...fieldSelections,
                      [item.field.id]: !fieldSelections[item.field.id],
                    });
                  }
                }}
                columns={[
                  {
                    key: "type",
                    name: "Type",
                    render: ({ item }) =>
                      item.field.type === "assessment" ? "Score" : "Feedback",
                  },
                  {
                    key: "sectionName",
                    name: "Section",
                    render: ({ item }) => item.sectionName,
                  },
                  {
                    key: "field",
                    name: "Field",
                    render: ({ item }) => item.field.title || "Untitled field",
                  },
                ]}
              />
            </>
          : <>
              <p style={{ marginBottom: 12 }}>
                Review and edit the email content below before sending it to the{" "}
                {t("generic.idea")} author.
              </p>

              <Form>
                <Form.Field
                  control={RichText}
                  label="Feedback email content"
                  preset="simple"
                  autoHideMenu={false}
                  value={feedbackHtml}
                  onChange={(html: string) => setFeedbackHtml(html)}
                />
              </Form>
            </>
          }
        </Modal.Content>
        <Modal.Actions>
          {step === "configure" ?
            <>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              <Button primary onClick={review}>
                Review
              </Button>
            </>
          : <>
              <Button
                onClick={() => {
                  setStep("configure");
                }}
              >
                Back
              </Button>
              <Button
                onClick={() => {
                  util
                    .confirm(
                      "Reset feedback",
                      "Are you sure you want to reset the feedback to the previously generated version?",
                    )
                    .then(() => {
                      setFeedbackHtml(generatedHtml);
                    })
                    .catch(() => {});
                }}
              >
                <Icon name="undo" />
                Reset
              </Button>
              <Button primary onClick={send} disabled={!feedbackHtml}>
                <Icon name="send" />
                Send
              </Button>
            </>
          }
        </Modal.Actions>
      </Modal>
    </>
  );
};
